import { useRef, useState, useEffect, useCallback } from 'react';

interface ImageCropperProps {
  imageFile: File;
  onCrop: (blob: Blob) => void;
  onCancel: () => void;
}

export default function ImageCropper({ imageFile, onCrop, onCancel }: ImageCropperProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const CANVAS_SIZE = 280;
  const CIRCLE_RADIUS = 120;

  useEffect(() => {
    const url = URL.createObjectURL(imageFile);
    const image = new Image();
    image.onload = () => {
      setImg(image);
      const minDim = Math.min(image.width, image.height);
      const initialScale = (CIRCLE_RADIUS * 2) / minDim;
      setScale(initialScale);
      setOffset({
        x: CANVAS_SIZE / 2 - (image.width * initialScale) / 2,
        y: CANVAS_SIZE / 2 - (image.height * initialScale) / 2,
      });
    };
    image.src = url;
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    ctx.fillStyle = '#0b0b0e';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    ctx.save();
    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CIRCLE_RADIUS, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(img, offset.x, offset.y, img.width * scale, img.height * scale);
    ctx.restore();

    ctx.save();
    ctx.fillStyle = 'rgba(11, 11, 14, 0.7)';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CIRCLE_RADIUS, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    ctx.strokeStyle = 'rgba(113, 113, 122, 0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CIRCLE_RADIUS, 0, Math.PI * 2);
    ctx.stroke();
  }, [img, offset, scale]);

  useEffect(() => {
    draw();
  }, [draw]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setDragging(true);
    setDragStart({ x: touch.clientX - offset.x, y: touch.clientY - offset.y });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!dragging) return;
    const touch = e.touches[0];
    setOffset({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y,
    });
  };

  const handleCrop = () => {
    if (!img) return;
    const output = document.createElement('canvas');
    const outputSize = 512;
    output.width = outputSize;
    output.height = outputSize;
    const ctx = output.getContext('2d');
    if (!ctx) return;

    const sourceX = (CANVAS_SIZE / 2 - CIRCLE_RADIUS - offset.x) / scale;
    const sourceY = (CANVAS_SIZE / 2 - CIRCLE_RADIUS - offset.y) / scale;
    const sourceSize = (CIRCLE_RADIUS * 2) / scale;

    ctx.beginPath();
    ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(img, sourceX, sourceY, sourceSize, sourceSize, 0, 0, outputSize, outputSize);

    output.toBlob(
      (blob) => {
        if (blob) onCrop(blob);
      },
      'image/png',
      0.9
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="rounded-lg p-6 max-w-sm w-full mx-4" style={{ background: 'var(--dash-card)', border: '1px solid var(--dash-border)' }}>
        <h3 className="font-mono text-[10px] uppercase tracking-widest mb-4" style={{ color: 'var(--dash-text-dim)' }}>
          Adjust Photo
        </h3>

        <div className="flex justify-center mb-4">
          <canvas
            ref={canvasRef}
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
            className="rounded cursor-grab active:cursor-grabbing"
            style={{ width: CANVAS_SIZE, height: CANVAS_SIZE }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={() => setDragging(false)}
          />
        </div>

        <div className="flex items-center gap-3 mb-5">
          <span className="font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>-</span>
          <input
            type="range"
            min="0.1"
            max="3"
            step="0.01"
            value={scale}
            onChange={(e) => {
              const newScale = parseFloat(e.target.value);
              const cx = CANVAS_SIZE / 2;
              const cy = CANVAS_SIZE / 2;
              const imgCenterX = (cx - offset.x) / scale;
              const imgCenterY = (cy - offset.y) / scale;
              setScale(newScale);
              setOffset({
                x: cx - imgCenterX * newScale,
                y: cy - imgCenterY * newScale,
              });
            }}
            className="flex-1 accent-zinc-500 h-1"
          />
          <span className="font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>+</span>
        </div>

        <p className="font-sans text-[11px] text-center mb-4" style={{ color: 'var(--dash-text-faint)' }}>
          Drag to reposition. Use slider to resize.
        </p>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 font-mono text-sm py-2 rounded transition-colors uppercase tracking-wider"
            style={{
              border: '1px solid var(--dash-border)',
              color: 'var(--dash-text-muted)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--dash-border)';
              e.currentTarget.style.color = 'var(--dash-text-secondary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--dash-border)';
              e.currentTarget.style.color = 'var(--dash-text-muted)';
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleCrop}
            className="flex-1 bg-blue-600 text-white font-mono text-sm py-2 rounded hover:bg-blue-500 transition-colors uppercase tracking-wider"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
